import { getBillingData } from './api';
import { isStandaloneMode, getTestJWT, getStandaloneForceUpgradeResponse } from '../utils/env';
import { ADMIN_NAVIGATION_DESTINATIONS } from '../utils/constants';
import { recordBillingReady } from '../utils/billing-diagnostics';

const ALLOWED_INCOMING_QUERIES = new Set(['getSubscription', 'limitUpdate', 'routeUpdate']);

const ALLOWED_ADMIN_DESTINATIONS = new Set(Object.values(ADMIN_NAVIGATION_DESTINATIONS));

/**
 * @typedef {object} ForceUpgradeInfo
 * @property {boolean} isOwner
 * @property {boolean} forceUpgrade
 * @property {{name: string, email: string}} ownerUser
 */

const getParentWindowOrigin = () => {
    const ancestorOrigin = window.location?.ancestorOrigins?.[0];
    if (ancestorOrigin) {
        return ancestorOrigin;
    }

    try {
        return document.referrer ? new URL(document.referrer).origin : null;
    } catch {
        return null;
    }
};

const isTrustedParentMessage = (event) => event?.origin === getParentWindowOrigin();

class ParentWindowDataChannel {
    constructor() {
        this.subscribers = Object.create(null);

        // Only listen for postMessage events in iframe mode
        if (!isStandaloneMode()) {
            window.addEventListener('message', this.handleIncomingMessage.bind(this));
        }
    }

    subscribe(queryName, cb) {
        this.subscribers[queryName] = cb;
    }

    handleIncomingMessage(event) {
        if (!event || !event.data || !isTrustedParentMessage(event)) {
            return;
        }

        const messageData = event.data;
        const queryType = messageData.query;

        if (!ALLOWED_INCOMING_QUERIES.has(queryType)) {
            return;
        }

        const subscriber = this.subscribers[queryType];

        if (typeof subscriber !== 'function') {
            return;
        }

        // Ghost Admin can send us route updates to enforce a redirect, as
        // child routes can't be set from within the Admin.
        if (queryType === 'routeUpdate') {
            if (messageData.response) {
                return subscriber(messageData.response);
            }

            return;
        }

        if (queryType === 'limitUpdate') {
            return subscriber();
        }

        const responseKey =
            typeof messageData.response === 'string' ? messageData.response : 'subscription';
        const responseHandler = (data) => {
            this.sendMessage({
                [responseKey]: data,
            });
        };

        return subscriber(responseHandler);
    }

    /**
     * @param {Record<string, unknown>|null|undefined} data - Message for Ghost Admin
     * @returns {boolean} Whether the message was posted to Ghost Admin
     */
    sendMessage(data) {
        if (data?.error) {
            // don't try sending errors to the Ghost Admin
            return false;
        }

        // In standalone mode, we don't have a parent window to send messages to
        if (isStandaloneMode()) {
            return false;
        }

        // A top-level window is its own parent, so there is no Admin to post to
        if (window.parent === window) {
            return false;
        }

        const targetOrigin = getParentWindowOrigin();
        if (!targetOrigin) {
            return false;
        }

        window.parent.postMessage(data, targetOrigin);
        return true;
    }

    /**
     * @param {object} options
     * @param {string} options.route
     * @param {string} options.state
     * @returns {void}
     */
    sendBillingAppReady({ route, state }) {
        const timestamp = Date.now();
        const posted = this.sendMessage({
            request: 'billingAppReady',
            route,
            state,
            release: process.env.REACT_APP_COMMIT_REF,
            timestamp,
        });

        if (posted) {
            recordBillingReady(timestamp);
        }
    }

    /**
     * @description Asks Ghost Admin to open one of its approved settings screens.
     * Ghost Admin owns the destination-to-route mapping (see GVA-868); we only
     * ever send a whitelisted semantic identifier, never a raw Admin route or URL.
     * Unknown, non-string, or missing destinations are ignored silently.
     * @param {string} destination - One of ADMIN_NAVIGATION_DESTINATIONS values.
     * @returns {void}
     */
    sendNavigateToAdmin(destination) {
        if (!ALLOWED_ADMIN_DESTINATIONS.has(destination)) {
            return;
        }

        this.sendMessage({ request: 'navigateToAdmin', destination });
    }

    async getToken() {
        // In standalone mode, return test JWT immediately without postMessage
        if (isStandaloneMode()) {
            return Promise.resolve(getTestJWT());
        }

        const sendMessage = this.sendMessage;

        return new Promise(function (resolve) {
            window.addEventListener('message', function self(event) {
                if (isTrustedParentMessage(event) && event.data && event.data.request === 'token') {
                    window.removeEventListener('message', self);

                    resolve(event.data.response);
                }
            });

            sendMessage({ request: 'token' });
        });
    }

    /**
     * @description Fetches force upgrade info from Ghost Admin via postMessage
     * @returns {Promise<ForceUpgradeInfo>}
     */
    async getForceUpgradeInfo() {
        // In standalone mode, return test config immediately without postMessage
        if (isStandaloneMode()) {
            return Promise.resolve(getStandaloneForceUpgradeResponse());
        }

        // Create an AbortController which allows us to cancel
        // the eventListener and avoids waiting forever for the
        // message like some princesses wait for their prince.
        const controller = new AbortController();
        const sendMessage = this.sendMessage;

        return new Promise(function (resolve) {
            window.addEventListener(
                'message',
                function self(event) {
                    if (
                        isTrustedParentMessage(event) &&
                        event.data &&
                        event.data.request === 'forceUpgradeInfo'
                    ) {
                        window.removeEventListener('message', self);

                        resolve(/** @type {ForceUpgradeInfo} */ (event.data.response));
                    }
                },
                { signal: controller.signal },
            );

            sendMessage({ request: 'forceUpgradeInfo' });

            setTimeout(() => {
                controller.abort();
                resolve({});
            }, 2000);
        });
    }

    /**
     * Sends the complete BillingData payload to Ghost Admin without projecting or wrapping it.
     * Ghost consumes top-level `isGrace` as Billing's dunning state and
     * `subscription.isActiveTrial` for its trial UI; neither is derived here from Stripe status.
     * Daisy's failed-payment count remains at `user.payment_attempts`: numeric zero is valid,
     * and an absent value stays absent. Ghost Admin owns `hostSettings.forceUpgrade`, so this
     * channel does not add it or relocate any of these fields.
     * @param {import('../types').BillingData} [billingData]
     * @returns {Promise<void>}
     */
    async sendSubscriptionData(billingData) {
        if (!billingData) {
            billingData = await getBillingData();
        }

        if (billingData?.error) {
            // don't try sending errors to the Ghost Admin
            return;
        }

        this.sendMessage(billingData);
    }
}

const parentWindowDataChannelSingleton = new ParentWindowDataChannel();

export default parentWindowDataChannelSingleton;
